import React from 'react';
import { StandardTableActions, ColoredBadge, StatusBadgeStylePicker } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { ServiceType } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import {
  normalizeHexColor,
  parseStatusColorConfig,
  serializeStatusColorConfig,
} from '../../../utils/statusColor';

const DEFAULT_SERVICE_TYPE_COLOR = '#64748b';

const getServiceTypeColor = (color?: string): string =>
  normalizeHexColor(parseStatusColorConfig(color).backgroundColor) ?? DEFAULT_SERVICE_TYPE_COLOR;

const renderServiceTypeBadge = (label?: string, color?: string) => (
  <ColoredBadge label={label || '-'} color={color || DEFAULT_SERVICE_TYPE_COLOR} />
);

export const moduleConfig = {
  module: {
    title: 'Service Types',
    description: 'Manage service types',
    permissionModuleKey: 'service_type',
    route: {
      list: '/service_type',
      new: '/service_type/new',
      detail: (id: number) => `/service_type/${id}`,
      edit: (id: number) => `/service_type/${id}/edit`,
    },
    api: {
      list: '/p-crm-service-type',
      detail: (id: number) => `/p-crm-service-type/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search service types...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['pst_descr'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'pst_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: ServiceType) => item.pst_descr,
        options: [],
      },
    ],
    applyFilter: (items: ServiceType[], filters: { pst_descr: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.pst_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.pst_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.pst_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Service Type',
        showTitle: false,
        variant: 'primary' as const,
        href: '/service_type/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: ServiceType) => {
      window.location.href = `/service_type/${item.id}`;
    },
    keyExtractor: (item: ServiceType) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new service type.',
    noSearchResults: 'No service types found matching your search criteria.',
    noFilterResults: 'No service types match the selected filters.',
  },
  delete: {
    title: 'Delete Service Type',
    message: 'Are you sure you want to delete this service type? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete service type. Please try again.',
    handler: async (id: number) => {
      const { deleteServiceType } = await import('../../../services/mutations');
      await deleteServiceType(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Service Type Details',
    notFoundTitle: 'Service Type not found',
    notFoundMessage: 'The service type you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Service Types',
    defaultTitle: 'Unnamed Service Type',
    getTitle: (item: ServiceType) => {
      return item.pst_descr || `Service Type #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Service Type',
      subtitle: 'Add a new service type',
      submitText: 'Create Service Type',
    },
    edit: {
      title: 'Edit Service Type',
      subtitle: 'Update service type information',
      submitText: 'Update Service Type',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading service type...',
    errorMessage: 'Failed to save service type. Please try again.',
    create: async (data: Partial<ServiceType>) => {
      const { createServiceType } = await import('../../../services/mutations');
      return await createServiceType(data);
    },
    update: async (id: number, data: Partial<ServiceType>) => {
      const { updateServiceType } = await import('../../../services/mutations');
      return await updateServiceType(id, data);
    },
  },
  fields: [
    {
      name: 'pst_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter service type description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
    {
      name: 'pst_color',
      label: 'Badge Style',
      type: 'custom',
      required: true,
      helperText: 'Pick the background color and text mode for the service type badge.',
      validation: {
        required: true,
      },
      customRender: ({ value, error, formData, setMultipleFieldValues }) => {
        const parsedConfig = parseStatusColorConfig(
          typeof value === 'string'
            ? value
            : serializeStatusColorConfig(DEFAULT_SERVICE_TYPE_COLOR, 'auto'),
        );

        return (
          <StatusBadgeStylePicker
            backgroundColor={parsedConfig.backgroundColor}
            textColorMode={parsedConfig.textColorMode}
            error={error}
            helperText="This will be used in service type badges and service type selection."
            previewLabel={formData?.pst_descr || 'Preview'}
            onChange={(backgroundColor, textColorMode) => {
              setMultipleFieldValues?.({
                pst_color: serializeStatusColorConfig(backgroundColor, textColorMode),
              });
            }}
          />
        );
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Service Type Information',
        icon: '📝',
        fields: ['pst_descr', 'pst_color'],
        columns: 2,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<ServiceType>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'pst_descr',
    header: 'Description',
    sortable: true,
    render: (value, row) => renderServiceTypeBadge(value, row.pst_color),
  },
  {
    key: 'pst_color',
    header: 'Color',
    sortable: true,
    render: (_value, row) => {
      const resolvedColor = getServiceTypeColor(row.pst_color);
      return (
        <div className="flex items-center gap-2">
          <span className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: resolvedColor }} />
          <span className="text-sm text-gray-700">{resolvedColor}</span>
        </div>
      );
    },
  },
];

export const getDetailSections = (
  item: ServiceType,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: renderServiceTypeBadge(item.pst_descr, item.pst_color),
        },
        {
          label: 'Color',
          value: (
            <div className="flex items-center gap-2">
              <span className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: getServiceTypeColor(item.pst_color) }} />
              <span>{getServiceTypeColor(item.pst_color)}</span>
            </div>
          ),
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: ServiceType) => {
  return renderServiceTypeBadge(item.pst_descr, item.pst_color);
};

export const getInitialFormData = (): Partial<ServiceType> => {
  return {
    pst_descr: '',
    pst_color: serializeStatusColorConfig(DEFAULT_SERVICE_TYPE_COLOR, 'auto'),
  };
};

export const mapItemToFormData = (item: ServiceType): Partial<ServiceType> => {
  return {
    pst_descr: item.pst_descr || '',
    pst_color: item.pst_color || serializeStatusColorConfig(DEFAULT_SERVICE_TYPE_COLOR, 'auto'),
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: ServiceType,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

